import { Alert, AlertColor, Box, Button, Divider, Drawer, Snackbar, Typography } from '@mui/material'
import { useCallback, useMemo, useState } from 'react'

import { UiContext } from './Context'

type SnackBarStatus = {
  open: boolean
  message: string
  severity?: AlertColor
}
const defaultSnackbarStatus = {
  open: false,
  message: '',
  severity: undefined,
}

type HelpDrawerStatus = {
  open: boolean
  title: string
  text: React.ReactNode
  buttonLabel: string
}
const defaultHelpDrawerStatus = {
  open: false,
  title: '',
  text: '',
  buttonLabel: '',
}

type UiContextProviderProps = {
  children: React.ReactNode
}

export const UiProvider = ({ children }: UiContextProviderProps) => {
  const [snackbar, setSnackbar] = useState<SnackBarStatus>(defaultSnackbarStatus)
  const [helpDrawer, setHelpDrawer] = useState<HelpDrawerStatus>(defaultHelpDrawerStatus)

  const onOpenSnackbar = useCallback((message: string, severity?: AlertColor): void => {
    setSnackbar({ message, severity, open: true })
  }, [])

  const onCloseSnackbar = useCallback(() => {
    setSnackbar(defaultSnackbarStatus)
  }, [])

  const onOpenHelpDrawer = useCallback((title: string, text: React.ReactNode, buttonLabel = 'Got it!'): void => {
    setHelpDrawer({ title, text, buttonLabel, open: true })
  }, [])

  const onCloseHelpDrawer = useCallback(() => {
    setHelpDrawer(defaultHelpDrawerStatus)
  }, [])

  const value = useMemo(
    () => ({ showSnackbar: onOpenSnackbar, showHelpDrawer: onOpenHelpDrawer }),
    [onOpenSnackbar, onOpenHelpDrawer],
  )

  return (
    <UiContext value={value}>
      {children}
      <Snackbar
        aria-live="assertive"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        open={snackbar.open}
        autoHideDuration={10000}
        onClose={onCloseSnackbar}
        title={snackbar.severity ? snackbar.message : undefined}
      >
        {snackbar.severity && (
          <Alert severity={snackbar.severity} variant="filled">
            {snackbar.message}
          </Alert>
        )}
      </Snackbar>
      <Drawer open={helpDrawer.open} anchor="right" onClose={onCloseHelpDrawer} sx={{ zIndex: 9999 }} role="dialog">
        <Box sx={{ width: '400px', padding: 2 }}>
          <Typography variant="h3" component="h3" color="primary" gutterBottom>
            {helpDrawer.title}
          </Typography>
          <Divider
            sx={{
              mt: 1,
              mb: 1,
              opacity: '0.6',
            }}
          />
          <Typography variant="subtitle1">{helpDrawer.text}</Typography>
          <br />
          <Button color="secondary" onClick={onCloseHelpDrawer} variant="contained" sx={{ width: '100%' }}>
            {helpDrawer.buttonLabel}
          </Button>
        </Box>
      </Drawer>
    </UiContext>
  )
}
